import SkopeiConnectService from './skopeiConnectService';

type ServiceConstructor<T extends SkopeiConnectService> = new () => T;

type CacheEntry = {
	instance: SkopeiConnectService;
	serviceClass: ServiceConstructor<SkopeiConnectService>;
};

const cache = new Map<string, CacheEntry>();

/**
 * Returns a cached singleton instance of the given service class.
 *
 * Services are stateless after construction, so one instance per
 * ServiceClass is sufficient for the whole app.
 *
 * @example
 *   const organisations = getService(OrganisationsService);
 */
export function getService<T extends SkopeiConnectService>(ServiceClass: ServiceConstructor<T>): T {
	const key = ServiceClass.name;
	const cachedEntry = cache.get(key);

	// In HMR, classes can be replaced while cache survives. Recreate instance when constructor changes.
	const canReuseCachedInstance =
		cachedEntry &&
		cachedEntry.serviceClass === ServiceClass &&
		cachedEntry.instance instanceof ServiceClass;

	if (!canReuseCachedInstance) {
		const instance = new ServiceClass();
		cache.set(key, {
			instance,
			serviceClass: ServiceClass as ServiceConstructor<SkopeiConnectService>,
		});
		return instance as T;
	}

	return cachedEntry.instance as T;
}
